import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import ISO6391 from "iso-639-1";
import AsyncSelect from "react-select/async";
import { Form } from "react-bootstrap";
import "./GenerateOutline.css";
import axiosInstance from '../../utils/api';
import { generateBlogOutline, saveBlogDraftV2, handleShare } from '../../utils/blog';
import BlogComponent from '../../components/GenerateBlog/BlogComponent';
import { toast } from "react-toastify";
import UnifiedDocumentModal from '../../components/Common/UnifiedDocumentModal';


const GenerateOutline = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const clusterData = location.state?.clusterData;
  const sessionId = location.state?.session_id;
  const agentId = location.state?.agent_id;
  const UID = location.state?.uid || "";
  // Form state
  const [form, setForm] = useState({
    name: "",
    topic: "",
    tone: "casual",
    word_limit: "",
    location: "",
    language: "english",
    semantic_clusters: clusterData?.semantic_clusters || [],
    brand_context: "",
    exclude_topics: "",
    cta_mode: "",
    cta_product_name: "",
    cta_url: "",
    faq_count: "",
    format: "html"
  });

  const [selectedLocation, setSelectedLocation] = useState("");
  const [language, setLanguage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasCompletedOutline, setHasCompletedOutline] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [blogOutline, setBlogOutline] = useState(null);
  const [originalApiResponse, setOriginalApiResponse] = useState(null); // Store original full API response
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [blogId, setBlogId] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [allLocations, setAllLocations] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const languageOptions = useMemo(
    () => ISO6391.getAllNames().map(name => ({
      value: ISO6391.getCode(name),
      label: name
    })),
    []
  );

  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  // Handle form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };


  // Updated cleanupJsonObject function
  const cleanupJsonObject = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
      // Don't trim or modify string values
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => cleanupJsonObject(item));
    }

    return Object.entries(obj).reduce((acc, [key, value]) => {
      // Clean up only the key - remove quotes and extra spaces
      const cleanKey = key
        .trim()
        .replace(/^["']|["']$/g, '')  // Remove quotes
        .replace(/\s+/g, '')          // Remove all spaces from keys
        .replace(/_+/g, '_');         // Normalize underscores

      // Recursively clean up the value, but preserve spaces in strings
      acc[cleanKey] = cleanupJsonObject(value);
      return acc;
    }, {});
  };

  // Handle submit working
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setStreamingContent("");
    setStreamError(null);
    setIsStreaming(true);
    setHasCompletedOutline(false);
    setBlogOutline(null);
    
    try {
      const outlineResult = await generateBlogOutline(
        form.topic,
        form.tone,
        form.word_limit,
        selectedLocation?.label || form.location,
        language?.label || form.language,
        form.semantic_clusters,
        form.brand_context,
        form.exclude_topics,
        form.cta_mode,
        form.cta_product_name,
        form.cta_url,
        form.faq_count,
        sessionId,
        agentId,
        UID,
        form.topic, // Pass topic as query
        (chunk) => {
          setStreamingContent((prev) => prev + chunk);
        }
      );

      if (outlineResult) {
        // Store the original full API response
        setOriginalApiResponse(outlineResult);
        
        // Transform new response structure to old format for editing
        let transformedOutline = outlineResult;
        
        if (outlineResult.outline) {
          // New structure: transform to old format
          transformedOutline = {
            h1_title: outlineResult.outline.h1_title || "",
            h2_sections: outlineResult.outline.sections?.map((section) => ({
              section_title: section.h2 || "",
              content_scope: section.content_notes || "",
              focus_keywords: [
                ...(section.seo_targets?.primary || []),
                ...(section.seo_targets?.secondary || []),
                ...(section.seo_targets?.variants || []),
                ...(section.seo_targets?.questions || []),
              ],
              intent: section.intent || "",
              word_target: section.word_target || 100, // Default if not present
              format_type: section.format_type || "standard", // Default if not present
              h3_blocks: section.h3_blocks?.map((h3Item) => ({
                h3: h3Item.h3 || h3Item.title || "",
                hints: Array.isArray(h3Item.hints) ? h3Item.hints : (h3Item.hint ? [h3Item.hint] : []),
              })) || [],
            })) || [],
          };
        }
        
        setBlogOutline(transformedOutline);
        setHasCompletedOutline(true);
        navigate('/skills/blog/generate-blog', {
          state: {
              formData: {
              session_id: sessionId,
              agent_id: agentId,
              uid: UID,
              topic: form.topic,
              tone: form.tone,
              word_limit: form.word_limit,
              location: selectedLocation?.label || form.location,
              language: language?.label || form.language,
              semantic_clusters: form.semantic_clusters,
              brand_context: form.brand_context,
              exclude_topics: form.exclude_topics,
              cta_mode: form.cta_mode,
              cta_product_name: form.cta_product_name,
              cta_url: form.cta_url,
              faq_count: form.faq_count,
              blog_type: clusterData?.blog_type || "",
              format: form.format || "html",
            },
            blogOutline: transformedOutline,
            originalApiResponse: outlineResult // Pass original full response
          }
        });
      } else {
        setStreamError("Failed to parse response data. Please try again.");
      }
      setIsStreaming(false);
      setLoading(false);
    } catch (err) {
      console.error("Blog generation error:", err);
      setError(err.message || "Failed to generate blog");
      setStreamError(err);
      setIsStreaming(false);
      setLoading(false);
    }
  };

  const handleRegenerateText = (sectionIndex) => {
    // Implement regenerate logic for specific section
  };

  const handleEditText = (idx, newContent) => {
    setBlogOutline(prev => {
      const updated = { ...prev };
      // Handle both new and old structure
      if (updated.outline?.sections) {
        // New structure
        updated.outline.sections = [...updated.outline.sections];
        updated.outline.sections[idx] = { 
          ...updated.outline.sections[idx], 
          content_notes: newContent 
        };
      } else if (updated.h2_sections) {
        // Old structure
        updated.h2_sections = [...updated.h2_sections];
        updated.h2_sections[idx] = { ...updated.h2_sections[idx], content_scope: newContent };
      }
      return updated;
    });
  };
  // Defensive normalization for blogOutline.sections
  let normalizedSections = [];
  if (blogOutline && blogOutline.sections) {
    if (Array.isArray(blogOutline.sections)) {
      normalizedSections = blogOutline.sections;
    } else if (typeof blogOutline.sections === 'object') {
      normalizedSections = [blogOutline.sections];
    }
  }
  // Save as draft handler
  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      if (!form.topic) {
        toast.error("Topic can't be blank");
        return;
      }

      const result = await saveBlogDraftV2({
        name: form.name || 'Blog',
        tone: form.tone,
        about: form.topic,
        word_limit: form.word_limit,
        location: selectedLocation?.label || form.location,
        language: language?.label || form.language,
        further_details: "",
        status: 'draft',
        keyword_clusters: JSON.stringify(form.semantic_clusters) || '',
        outlines: blogOutline ? JSON.stringify(blogOutline) : '',
        session_id: sessionId,
        agent_id: agentId,
        uid: UID,
      });
      setBlogId(result.blog?.id || result.blog?._id);
      toast.success('Draft saved successfully!');
    } catch (err) {
      toast.error('Failed to save draft: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <div className="generate-blog-container">
       <div class="breadcrumb-container mx-3">
          <div class="breadcrumb-back">
            <span class="back-text" onClick={() => navigate(-1)}>&lt; Back</span>
            </div>
              <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                  <li class="breadcrumb-item">
                    <a href="/">Home</a></li>
                    <span class="breadcrumb-separator"> &gt; </span>
                    <li class="breadcrumb-item ">
                      <a href="/skills">Skills</a></li>
                      <span class="breadcrumb-separator"> &gt; </span>
                    <li class="breadcrumb-item ">
                      <a href="/skills/blog">Blog templates</a></li>
                      <span class="breadcrumb-separator"> &gt; </span>
                    <li class="breadcrumb-item active">
                      <span>Generate New Blog</span>
                    </li>
                </ol>
              </nav>
            </div>
     
            <div class="d-flex justify-content-between mb-3">
              <div class="title-label">Generate outline based on Keyword cluster</div>
                <div class="d-flex gap-2">
                  <div><button class="btn bgprimary" onClick={() => handleShare(blogId)}>Share</button>
                </div>
              <div>
            <button class="btn btn-dark rounded" onClick={handleSaveDraft} disabled={isSavingDraft}>Save as draft</button>
          </div>
        </div>
      </div>
     
<div className="row">
      <div className="col-md-4">
      <h2 class="form-title">Please fill further information as relevant</h2>
      <Form onSubmit={handleSubmit}>
        <div className="mainform edit-product-left">
            <div className="form-section ">
              <label className="form-label">Topic <span className="text-danger">*</span></label>
              <Form.Control
                as="textarea"
                name="topic"
                value={form.topic}
                onChange={handleChange}
                required
                placeholder="Enter topic"
              />
            </div>

            <div className="form-section">
          <label className="form-label">What should be the tone of voice?</label>
              <input
                list="tone-options"
                name="tone"
                value={form.tone}
                onChange={handleChange}
                className="form-control"
              />
              <datalist id="tone-options">
                <option value="Casual" />
                <option value="Professional" />
                <option value="Friendly" />
                <option value="Formal" />
                <option value="Informal" />
                <option value="Persuasive" />
              </datalist>
        </div>

            <div className="form-section">
          <label className="form-label">Word Count Target</label>
          <Form.Control
            type="number"
            name="word_limit"
            value={form.word_limit}
            onChange={handleChange}
            placeholder="Enter word count target"
          />
        </div>

            <div className="form-section">
          <label className="form-label">Location focus if any</label>
              <AsyncSelect
                cacheOptions
                loadOptions={loadLocationOptions}
                value={selectedLocation}
                onChange={setSelectedLocation}
                isClearable
                isSearchable
                placeholder="Type to search location..."
               
                noOptionsMessage={({ inputValue }) => 
                  !inputValue 
                    ? "Type to search..." 
                    : "No locations found for that query"
                }
                loadingMessage={() => "Searching locations..."}
                components={{
                  DropdownIndicator: () => null, // Hide dropdown arrow
                  IndicatorSeparator: () => null // Hide separator
                }}
              />
        </div>

        <div className="form-section">
          <label className="form-label">Language</label>
              <Select
                options={languageOptions}
                value={language}
                onChange={setLanguage}
                isClearable
                isSearchable
                placeholder="Select language..."
               
              />
            </div>

            <div className="form-section">
              <label className="form-label">Brand Context</label>
              <Form.Control
                as="textarea"
                name="brand_context"
                value={form.brand_context}
                onChange={handleChange}
                placeholder="Enter brand_context"
              />
            </div>
            
            <div className="form-section">
              <label className="form-label">Exclude Topics</label>
              <Form.Control
                as="textarea"
                name="exclude_topics"
                value={form.exclude_topics}
                onChange={handleChange}
                placeholder="Enter exclude_topics"
              />
            </div>
            
            <div className="form-section">
              <label className="form-label">CTA Mode</label>
              <Form.Control
                type="text"
                name="cta_mode"
                value={form.cta_mode}
                onChange={handleChange}
                placeholder="Enter cta_mode"
              />
            </div>
            
            <div className="form-section">
              <label className="form-label">CTA Product Name</label>
              <Form.Control
                type="text"
                name="cta_product_name"
                value={form.cta_product_name}
                onChange={handleChange}
                placeholder="Enter cta_product_name"
              />
            </div>
            
            <div className="form-section">
              <label className="form-label">CTA URL</label>
              <Form.Control
                type="text"
                name="cta_url"
                value={form.cta_url}
                onChange={handleChange}
                placeholder="Enter cta_url"
              />
            </div>
            
            <div className="form-section">
          <label className="form-label">FAQ Count</label>
          <Form.Control
            type="number"
            name="faq_count"
            value={form.faq_count}
            onChange={handleChange}
            placeholder="Enter faq_count"
          />
        </div>

          {/*  <div className="form-section">
          <label className="form-label">Format</label>
          <Form.Select
            name="format"
            value={form.format}
            onChange={handleChange}
          >
            <option value="html">HTML</option>
            <option value="markdown">Markdown</option>
          </Form.Select>
        </div> */}
            </div> 
            <button
              type="submit"
              className="optimize-button mt-2"
              disabled={loading}
            >
              {loading ? "Generating..." : "Generate Outline"}
            </button>
            <button type="button" className="documents-button" onClick={() => setShowDocumentModal(true)}>
              <i className="fas fa-plus"></i> Any additional documents
            </button>
          </Form>
      </div>
        <div className="col-md-8 border-start">
        <h2 class="form-title">Blog Outline</h2>
         
          {error && <div className="error-message">{error}</div>}
          {(isStreaming || (streamingContent && !hasCompletedOutline)) && (
            <div className="streaming-content">
              <div className="loading-indicator">
                <div className="typing-dots"></div>
              </div>
              <pre>{streamingContent}</pre>
            </div>
          )}
          {hasCompletedOutline && blogOutline ? (
            <BlogComponent
              topic_map={blogOutline}
              onRegenerate={handleRegenerateText}
              onEdit={handleEditText}
            />
          ) : !isStreaming && !error ? (
            <div className="placeholder-text">
            <img src="/blog-placeholder.png"/>
            </div>
          ) : null}
        </div>
        </div>

      <UnifiedDocumentModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        initialTags={""}
        initialDocList={""}
      />
    </div>
  );
};

export default GenerateOutline;